import { useCallback, useEffect, useState } from 'react';

import { Box } from '#/components/Box/Box';
import { useUser } from '#/contexts/UserEnvironmentContext';
import {
  cmsApiSignUpForMovement,
  useCmsApiGetMovementSignupCount,
} from '#/modules/cms/api/cmsApiMovementsEndpoints';
import type {
  CmsApiSplitBlockAppearance,
  CmsApiSplitBlockMovement,
} from '#/modules/cms/api/types/blocks/CmsApiSplitContentBlock';
import { gutterWidth } from '#/theme/theme';
import { showModal } from '#/zustand-stores/modalStore';
import {
  setPreSignupJoinedMovement,
  usePreSignupJoinedMovement,
} from '#/zustand-stores/preSignupJoinMovementStore';

import { CmsSplitContentTextContentInner } from './CmsSplitContentTextContentInner';

type Props = {
  section: CmsApiSplitBlockMovement;
  appearance: CmsApiSplitBlockAppearance;
  index: number;
  blockId: string;
  imageTextBlock: boolean;
  showBackgroundImage: boolean;
  blockIndex: number;
  pageUpdated: string;
};

export function CmsSplitContentMovementContent({
  appearance,
  blockId,
  blockIndex,
  imageTextBlock,
  showBackgroundImage,
  index,
  pageUpdated,
  section,
}: Props) {
  const showTextBox = ['FULL_WIDTH', 'OVERLAPPING'].includes(appearance);

  const { preSignupMovement } = usePreSignupJoinedMovement();
  const [unfinishedJoinedMovement, setUnfinishedJoinedMovement] =
    useState(false);

  const [
    movementCountData = {
      count: section.data.count,
      userSignedUp: section.data.userSignedUp,
    },
    { updateCache, reload },
  ] = useCmsApiGetMovementSignupCount(section.data.slug);

  const { user } = useUser();

  const movementSignupConfirmed = useCallback(
    async () =>
      cmsApiSignUpForMovement(section.data.slug, blockId, index).then(
        (updatedCountData) => {
          updateCache(updatedCountData);
        },
      ),
    [blockId, index, section.data.slug, updateCache],
  );

  const movementSignUp = useCallback(() => {
    showModal('JOIN_MOVEMENT', {
      title: section.data.confirmationTitle,
      buttonText: section.data.buttonText,
      body: section.data.confirmationBody,
      onConfirm: movementSignupConfirmed,
    });
  }, [
    movementSignupConfirmed,
    section.data.buttonText,
    section.data.confirmationBody,
    section.data.confirmationTitle,
  ]);

  useEffect(() => {
    reload();
  }, [reload, user]);

  useEffect(() => {
    if (
      user?.emailVerified &&
      user?.firstName &&
      user?.lastName &&
      user?.hasPassword &&
      preSignupMovement === section.data.slug
    ) {
      setUnfinishedJoinedMovement(true);
    }
  }, [
    user?.emailVerified,
    user?.firstName,
    user?.lastName,
    user?.hasPassword,
    preSignupMovement,
    section.data.slug,
  ]);

  useEffect(() => {
    if (unfinishedJoinedMovement) {
      movementSignUp();
      setPreSignupJoinedMovement(null);
      setUnfinishedJoinedMovement(false);
    }
  }, [unfinishedJoinedMovement, movementSignUp]);

  return (
    <Box
      qaId={`split-content-section-${index}`}
      // @ts-expect-error TS(2322): Type 'false | 1' is not assignable to type 'Respon... Remove this comment to see the full error message
      zIndex={showTextBox && 1}
      width={[showBackgroundImage ? null : 1, null, 1 / 2]}
      margin={[showBackgroundImage ? '24px' : null, null]}
      px={[imageTextBlock ? '24px' : gutterWidth / 4, null, gutterWidth / 2]}
      py={[
        imageTextBlock ? gutterWidth / 2 : gutterWidth / 4,
        null,
        gutterWidth / 2,
      ]}
      key={blockId}
    >
      <CmsSplitContentTextContentInner
        appearance={appearance}
        blockId={blockId}
        blockIndex={blockIndex}
        index={index}
        pageUpdated={pageUpdated}
        label={
          movementCountData?.userSignedUp
            ? section.data.afterLabel
            : section.data.beforeLabel
        }
        headline={
          movementCountData?.userSignedUp
            ? section.data.afterHeadline
            : section.data.beforeHeadline
        }
        html={
          movementCountData?.userSignedUp
            ? section.data.afterHtml
            : section.data.beforeHtml
        }
        button={null}
        hideButton
        showTextBox={showTextBox}
        showBackgroundImage={showBackgroundImage}
        movementSignupButtonText={section.data.buttonText}
        movementUserLoggedIn={Boolean(user)}
        movementUserSignedUp={movementCountData?.userSignedUp}
        movementSignUp={movementSignUp}
        movementCount={movementCountData?.count}
        movementSlug={section.data.slug}
      />
    </Box>
  );
}
